import Head from 'next/head'
import Link from 'next/link'

import Header from '../../components/newHeader/Header'
import PuppyButton from '../../components/puppyButton'
import LoginRedirect from '../../components/loginRedirect'
import Footer from '../../components/newFooter'

import styles from './breeder.module.scss'

export default function BreederLogin () {
  return (
    <div>
      <Head>
        {/*  Facebook tags  */}
        <meta name='og:title' content='Join Our Breeder Community | MawooPets.com' key='title' />
        <meta name='og:type' content='website' key='type' />
        <meta name='og:url' content={`${process.env.NEXT_PUBLIC_ROOT_URL}/join-us-breeder`} key='url' />
        <meta name='og:description' content='Are you a caring breeder? Mawoo Petsʼs mission is to help you find forever homes for your puppies. Learn about the benefits of joining our community of responsible breeders, and submit an application today.' />
        <meta name='og:locale' content='en_US' key='locale' />
        {/*  standard tags  */}
        <title>Join Our Breeder Community | MawooPets.com</title>

        <meta name='description' content='Are you a caring breeder? Mawoo Petsʼs mission is to help you find forever homes for your puppies. Learn about the benefits of joining our community of responsible breeders, and submit an application today.' />
        <meta charSet='UTF-8' />
        <meta httpEquiv='X-UA-Compatible' content='IE=edge' />
        <meta name='viewport' content='width=device-width, initial-scale=1.0' />
        <meta name='robots' content={process.env.NEXT_PUBLIC_ROOT_URL === 'https://www.mawoopets.com' ? 'index, follow' : 'noindex, nofollow'} />
        <link rel='canonical' href={`${process.env.NEXT_PUBLIC_ROOT_URL}/join-us-breeder`} />
      </Head>

      <main className={styles.main}>
        <LoginRedirect />
        <Header />
        <div className={styles.content}>
          <section className={styles.hero} id='breederLandingHero'>
            <div className={styles.heroHolder}>
              <div className={styles.leftCol}>
                <div className={styles.leftColHolder}>
                  <h1 className={styles.heroTitle}>We help breeders find customers, for free</h1>
                  <div className={styles.heroSubtitle}>Are you a registered or family operated kennel? Apply to join our community</div>
                  <div className={styles.buttonHolder}>
                    <Link href='/signup/breeder' legacyBehavior>
                      <PuppyButton theme='red' title='Sign up for FREE' />
                    </Link>
                  </div>
                </div>
              </div>
              <div className={styles.rightCol}>
                <div className={styles.heroImage} title="Your Kennel's Dashboard" role='img' aria-label='Your Kennel`s Dashboard' />
              </div>
            </div>
          </section>
          <section className={styles.showcase}>
            <div className={styles.content}>
              <h2 className={styles.title}>Showcasing your Profile</h2>
              <div className={styles.subtitle}>Join a community of passionate reputable breeders following the industry's best practices</div>
              <div className={styles.list}>
                <div className={styles.item}><div className={styles.check} />Breeder History & Experience</div>
                <div className={styles.item}><div className={styles.check} />Club Certifications</div>
                <div className={styles.item}><div className={styles.check} />Kennel Certificates</div>
                <div className={styles.item}><div className={styles.check} />Vaccination Records</div>
                <div className={styles.item}><div className={styles.check} />Health & Safety Standards</div>
              </div>
            </div>
            <div className={styles.image} role='img' aria-label='Showcasing your Profile' />
          </section>
          <section className={styles.connect}>
            <div className={styles.content}>
              <h2 className={styles.title}>Connect with Customers</h2>
              <div className={styles.subtitle}>Your puppies can be seen by people regionally and nationally</div>
              <div className={styles.list}>
                <div className={styles.item}><div className={styles.check} />Video chat customers</div>
                <div className={styles.item}><div className={styles.check} />Schedule site visits</div>
                <div className={styles.item}><div className={styles.check} />Online chat with customers</div>
              </div>
            </div>
            <div className={styles.image} role='img' aria-label='Connect with Customers' />
          </section>
          <section className={styles.secure}>
            <div className={styles.content}>
              <h2 className={styles.title}>Secure<br />Transactions</h2>
              <div className={styles.subtitle}>We'll help you find the right owner for your puppy. Our buyers have:</div>
              <div className={styles.list}>
                <div className={styles.item}><div className={styles.check} />Applications for every dog - for your review.</div>
                <div className={styles.item}><div className={styles.check} />Pet Matcher to help customers find the right pet for them</div>
                <div className={styles.item}><div className={styles.check} />Price transparency on all puppies</div>
              </div>
            </div>
            <div className={styles.image} role='img' aria-label='Secure Transactions' />
          </section>
          <section className={styles.faq}>
            <h2 className={styles.title}>Frequently asked questions</h2>
            <div className={styles.list}>
              <div className={styles.item}>
                <div className={styles.question}>How does Mawoo Pets help me?</div>
                <div className={styles.answer}>We provide reputable breeders access to more customers. You can chat with new customers, schedule visits to your kennel, or a video conference with customers (for these socially distanced times). </div>
              </div>
              <div className={styles.item}>
                <div className={styles.question}>Do you have any fees for breeders?</div>
                <div className={styles.answer}>
                  No. We charge <b>no fees</b> to breeders. Our platform aims to provide reputable breeders greater visibility and customer connections. We want to provide this free of charge to breeders.
                  <br /> <br />
                  A 15% fee is charged to customers for the services we provide them: helping them find breeders in their area, facilitating connection with these breeders, automating electronic paperwork management for the puppies they purchase (vaccinations, vet checks, etc.), providing secure transactions with guarantees, etc.
                </div>
              </div>
              <div className={styles.item}>
                <div className={styles.question}>How do I sign up? </div>
                <div className={styles.answer}>You can <Link href='/signup/breeder'>apply here</Link> to join our community. We will schedule a time within 24 hours to discuss your application. </div>
              </div>
              <div className={styles.item}>
                <div className={styles.question}>Do customers fill out an application for our puppies? </div>
                <div className={styles.answer}>Yes. Customers fill out an application for every puppy they apply to. This application includes a standard set of questions designed by us, and can be augmented with your custom list of questions.  </div>
              </div>
              <div className={styles.item}>
                <div className={styles.question}>How do I upload my dogs onto the platform?</div>
                <div className={styles.answer}>Our breeder dashboard features a Pet Manager section, which allows you to upload your pets, including: breeds, size, images, descriptions, the parent profiles, etc. The upload process for images is made easy through our mobile-friendly platform, so you can snap pictures on your phone for an easy upload. </div>
              </div>
              <div className={styles.item}>
                <div className={styles.question}>How does a potential client find me? What is the process?</div>
                <div className={styles.answer}>Clients begin by searching for puppies of a breed in their Zip Code area. We showcase them profiles of breeders and their pets that match their needs. Customers can then start a conversation with you via chat, or schedule a site visit or video conversation with you. They can also make the purchase and pay directly to your bank account through our platform. </div>
              </div>
              <div className={styles.item}>
                <div className={styles.question}>Is there opportunity to modify the information such as the price after I have created the puppy profile? </div>
                <div className={styles.answer}>Yes, all information can be adjusted on an ongoing basis, including: name, size, weight, color, etc. The only exception is: once you receive an application for a puppy, its price can no longer be modified.</div>
              </div>
              <div className={styles.item}>
                <div className={styles.question}>How does a potential client find the Mawoo Pets website? Where are you marketing? </div>
                <div className={styles.answer}>Customers find our website through digital media channels. We sponsor advertisements across search & social media platforms to attract customers seeking to buy a pet -- within both Canada and the US. </div>
              </div>
            </div>
          </section>
          <section className={styles.banner}>
            <h2 className={styles.title}>A website for caring breeders</h2>
            <div className={styles.subtitle}>Join a community of reputable breeders looking for trusted customers</div>
            <div className={styles.buttonHolder}>
              <Link href='/signup/breeder' legacyBehavior>
                <PuppyButton theme='red' title='Apply Now' />
              </Link>
            </div>

          </section>
        </div>
        <Footer />
      </main>
    </div>
  )
}
